import type { BlogFrontmatter } from "./types";

/**
 * Bild-Assets (Visual-Regel 12, final):
 * - Motiv 1 (OG/Thumbnail/Hero, 1200×630 = 1,91:1): Ohne Zielliste ist ABM
 *   nur teures Streuen.
 * - Motiv 2 (Inline-Creative, 1200×630): Firmografie, Signale und Reifegrad
 *   als ICP-Dimensionen.
 * Dateien unverändert lassen, nichts neu rendern.
 */
const MOTIV_1_OG = "/assets/blog/icp-erstellen-abm-zielliste/motiv-1-og.png";
const MOTIV_1_OG_ALT = "Ohne Zielliste ist ABM nur teures Streuen";
const MOTIV_2_VERGLEICH =
  "/assets/blog/icp-erstellen-abm-zielliste/motiv-2-vergleich.png";

export const frontmatter: BlogFrontmatter = {
  title: "ICP erstellen und ABM-Zielliste aufbauen: Schritt für Schritt",
  metaTitle: "ICP erstellen: ABM-Zielliste Schritt für Schritt",
  slug: "icp-erstellen-abm-zielliste",
  description:
    "ICP erstellen und ABM-Zielliste aufbauen: Kriterien, Datenquellen, Priorisierung. So fokussieren Sie Budget auf die Accounts, die wirklich zählen.",
  primaryKeyword: "icp erstellen",
  secondaryKeywords: [
    "account based marketing b2b",
    "abm zielliste",
    "ideal customer profile b2b",
  ],
  author: "moritz",
  datePublished: "2026-07-03",
  dateModified: "2026-07-10",
  image: MOTIV_1_OG,
  imageAlt: MOTIV_1_OG_ALT,
  heroImage: {
    src: MOTIV_1_OG,
    alt: MOTIV_1_OG_ALT,
    width: 1200,
    height: 630,
  },
  internalLinks: ["/abm", "/blog/b2b-leadgenerierung-linkedin"],
  draft: false,
  readingMinutes: 6,
  kurzGesagt:
    "Ein Ideal Customer Profile (ICP) definiert, welche Firmen wirklich zu Ihnen passen, nach festen, überprüfbaren Kriterien. Die daraus abgeleitete ABM-Zielliste ist das Fundament jeder planbaren Kampagne. Ohne sie streuen Ads und Outbound wirkungslos in die Breite.",
  faq: [
    {
      q: "Wie erstellt man ein ICP?",
      a: "Rückwärts, nicht aus dem Wunschdenken heraus. Sie analysieren Ihre besten Bestandskunden (die, die am schnellsten abgeschlossen haben und am längsten bleiben) und extrahieren deren gemeinsame Merkmale. Diese Muster übersetzen Sie in feste, überprüfbare Kriterien entlang dreier Dimensionen: firmografisch, technografisch und verhaltensbasiert. Das Ergebnis ist kein Bauchgefühl, sondern ein Filter, den jeder im Team gleich anwendet.",
    },
    {
      q: "Wie groß sollte eine ABM-Zielliste sein?",
      a: "Klein genug, dass Sie jeden Account ernsthaft bearbeiten können. Für die meisten mittelständischen B2B-Teams sind das eher einige hundert als mehrere tausend Firmen. Eine Zielliste, die so lang ist, dass niemand sie je durchtelefoniert, ist keine Fokussierung, sondern eine getarnte Gießkanne. Lieber weniger Accounts mit hoher Passung als eine endlose Liste mit Streuverlust.",
    },
    {
      q: "Welche Datenquellen nutzt man?",
      a: "Basis ist Ihr eigenes CRM: Dort stecken die Muster Ihrer besten Kunden. Für den Aufbau der Longlist kommen Firmendatenbanken, LinkedIn Sales Navigator und Anreicherungs-Tools hinzu, die firmografische und technografische Daten liefern. Verhaltenssignale wie Website-Besuche oder Trigger-Events runden das Bild ab. Wichtig ist, dass alle Quellen in eine gepflegte Liste münden, nicht in fünf getrennte Tabellen.",
    },
    {
      q: "ICP oder Buyer Persona: was zuerst?",
      a: "Zuerst das ICP. Es beschreibt die Firma (den Account), die Buyer Persona beschreibt die Menschen darin. Im Account-based Marketing entscheiden Sie erst, welche Unternehmen Sie gewinnen wollen, und definieren dann, welche Rollen Sie dort ansprechen. Wer mit der Persona beginnt, optimiert die Ansprache, bevor das Ziel feststeht.",
    },
  ],
};

export default function Article() {
  return (
    <>
      <p>
        Die meisten ICPs sind Wunschdenken in einer Excel-Tabelle. Sie beschreiben
        den Kunden, den man gern hätte, nicht den, der tatsächlich schnell kauft und
        lange bleibt. Wer sein <strong>ICP erstellen</strong> will, sollte deshalb
        nicht bei der Vision beginnen, sondern bei den eigenen Daten.
      </p>
      <p>
        Unsere These: Ein belastbares Ideal Customer Profile entsteht rückwärts: aus
        den zwanzig Kunden, die am schnellsten geschlossen haben und am längsten
        bleiben. Alles andere ist geraten. Und ohne telefonische Qualifizierung
        bleibt selbst die beste Zielliste unvollständig, weil eine Firmenpassung auf
        dem Papier noch keinen echten Bedarf beweist.
      </p>

      <h2>Was ein ICP ist und was nicht</h2>
      <p>
        Ein Ideal Customer Profile ist die präzise Beschreibung des Firmentyps, der
        Ihren größten, schnellsten und langlebigsten Umsatz erzeugt, definiert über
        feste, überprüfbare Kriterien statt über Bauchgefühl. Es beschreibt den
        Account, nicht die Person: Im{" "}
        <a
          href="https://en.wikipedia.org/wiki/Account-based_marketing"
          target="_blank"
          rel="noopener noreferrer"
        >
          Account-based Marketing
        </a>{" "}
        (ein Begriff, den die ITSMA bereits 2003 prägte) ist die Firma die
        kleinste sinnvolle Einheit, nicht der einzelne Kontakt.
      </p>
      <p>
        Der Unterschied zur Buyer Persona ist wichtig: Das ICP filtert Firmen, die
        Persona beschreibt die Rollen darin. Sie brauchen beides, aber in dieser
        Reihenfolge.
      </p>
      <h3>Warum rückwärts aus den Daten, nicht vorwärts aus der Vision</h3>
      <p>
        Der übliche Weg ist der falsche: Ein Team setzt sich zusammen und beschreibt,
        welche Kunden es gern hätte. Das Ergebnis ist ein Profil, das die eigenen
        Ambitionen spiegelt, aber nicht die Realität des Vertriebs. Der bessere Weg
        führt über die eigene Historie. Ihre gewonnenen Deals enthalten die Antwort
        bereits. Sie müssen sie nur lesen.
      </p>
      <p>
        Deshalb starten Sie bei den zwanzig Kunden, die am schnellsten unterschrieben
        haben und am längsten bleiben. Kurze Sales-Cycles verraten, wo Ihr Angebot
        einen echten, dringenden Schmerz trifft. Lange Verweildauer verrät, wo es
        nachhaltig passt. Beides zusammen ergibt ein ICP, das nicht schön klingt,
        sondern funktioniert und dem Ihr Vertrieb vertraut, weil er die Firmen darin
        wiedererkennt.
      </p>

      <h2>ICP erstellen: 6 Schritte zur ABM-Zielliste</h2>
      <p>
        Die folgenden sechs Schritte führen von Ihren Bestandsdaten zu einer
        fokussierten, bearbeitbaren Zielliste.
      </p>
      <ol>
        <li>
          <strong>Beste Bestandskunden analysieren:</strong> Nehmen Sie die 20
          Kunden mit dem kürzesten Sales-Cycle und der längsten Verweildauer. Sie
          sind Ihr empirisches Fundament: nicht die größten Logos, sondern die
          reibungslosesten Abschlüsse.
        </li>
        <li>
          <strong>Gemeinsame Muster extrahieren:</strong> Welche Merkmale teilen
          diese Kunden? Achten Sie auf firmografische, technografische und
          verhaltensbasierte Gemeinsamkeiten (siehe Tabelle unten).
        </li>
        <li>
          <strong>Kriterien und Ausschlüsse festschreiben:</strong> Gießen Sie die
          Muster in harte Kriterien, inklusive klarer Ausschlusskriterien. Ein gutes
          ICP sagt genauso deutlich, wen Sie <em>nicht</em> wollen.
        </li>
        <li>
          <strong>Longlist aufbauen:</strong> Ziehen Sie über Firmendatenbanken,
          Sales Navigator und Anreicherung alle Firmen, die den Kriterien entsprechen.
          Das ist Ihre Grundgesamtheit.
        </li>
        <li>
          <strong>Priorisieren und eindampfen:</strong> Bewerten Sie die Longlist per
          Scoring nach Passung und Signalen und reduzieren Sie sie auf eine Zielliste,
          die Ihr Team tatsächlich bearbeiten kann.
        </li>
        <li>
          <strong>Übergeben und qualifizieren:</strong> Spielen Sie die Zielliste in
          Kampagne und Vertrieb aus und lassen Sie jeden interessierten Account
          telefonisch qualifizieren, bevor er als echte Chance gilt.
        </li>
      </ol>

      <h3>Die drei Kriterien-Dimensionen</h3>
      <p>
        Damit Schritt 2 und 3 nicht beliebig werden, gliedern Sie die Kriterien in
        drei Dimensionen:
      </p>
      <table>
        <thead>
          <tr>
            <th>Dimension</th>
            <th>Was sie beschreibt</th>
            <th>Beispiel-Kriterien</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td>Firmografisch</td>
            <td>Harte Stammdaten der Firma</td>
            <td>Branche, Mitarbeiterzahl, Umsatz, Region, Rechtsform</td>
          </tr>
          <tr>
            <td>Technografisch</td>
            <td>Eingesetzte Technologie</td>
            <td>CRM, Shop-/ERP-System, Marketing-Stack, Cloud-Nutzung</td>
          </tr>
          <tr>
            <td>Verhaltensbasiert</td>
            <td>Signale und Auslöser</td>
            <td>Website-Besuche, Content-Downloads, Trigger-Events wie Funding oder Neueinstellungen</td>
          </tr>
        </tbody>
      </table>

      <figure className="not-prose my-10">
        <img
          src={MOTIV_2_VERGLEICH}
          alt="Firmografie, Signale und Reifegrad als ICP-Dimensionen"
          width={1200}
          height={630}
          loading="lazy"
          className="w-full rounded-2xl"
        />
      </figure>

      <h2>Von der Longlist zur Zielliste: mit System priorisieren</h2>
      <p>
        Schritt 4 liefert eine Longlist, oft mehrere tausend Firmen, die die
        Grundkriterien erfüllen. Diese Liste ist noch keine Zielliste, sondern nur
        eine gefilterte Grundgesamtheit. Der entscheidende Schritt ist die
        Priorisierung: Nicht jede passende Firma ist gleich dringlich.
      </p>
      <p>
        Bewährt hat sich ein einfaches Scoring aus zwei Achsen. Die erste Achse ist
        die <strong>Passung</strong>: Wie genau trifft die Firma Ihr ICP über alle
        drei Dimensionen? Die zweite Achse sind <strong>Signale</strong>: Gibt es
        aktuelle Auslöser: ein Website-Besuch auf der Preisseite, eine passende
        Neueinstellung, eine Finanzierungsrunde? Firmen mit hoher Passung und frischen
        Signalen kommen nach oben, der Rest wartet.
      </p>
      <p>
        So entsteht aus der Longlist eine bearbeitbare Zielliste und, ebenso
        wichtig, eine begründete Reihenfolge. Ihr Vertrieb weiß dann nicht nur, wen er
        ansprechen soll, sondern auch, wen zuerst. Diese Priorisierung ist der
        Unterschied zwischen einer Liste, die im Sande verläuft, und einer, die
        abgearbeitet wird.
      </p>

      <h2>Die häufigsten Fehler beim ICP-Aufbau</h2>
      <p>
        Drei Fehler sehen wir immer wieder. Der erste ist das <strong>ICP aus dem
        Wunschdenken</strong>: Man beschreibt den Traumkunden, nicht den realen
        Bestbestand. Der zweite ist die <strong>fehlende Ausschlussliste</strong>:
        Wer nur definiert, wen er will, aber nicht, wen er ablehnt, filtert zu weich.
        Der dritte ist das <strong>Einfrieren</strong>: Ein ICP ist kein Denkmal. Ihre
        besten Kunden von heute verschieben sich, also gehört das Profil ein- bis
        zweimal im Jahr auf den Prüfstand.
      </p>

      <h2>Ohne Zielliste ist ABM nur teures Streuen</h2>
      <p>
        Der häufigste Fehler ist die zu große Liste. Ein Team startet mit tausenden
        „potenziell passenden" Firmen, verteilt Budget breit und wundert sich, dass
        nichts durchschlägt. Fokussierung ist kein Verzicht, sondern die eigentliche
        Wirkung von ABM: Dasselbe Budget auf wenige richtige Accounts konzentriert,
        erzeugt spürbar mehr als über viele falsche verteilt. Jeder Euro, der an eine
        unpassende Firma geht, fehlt bei einer passenden. Deshalb ist die schärfere
        Liste fast immer die profitablere.
      </p>
      <p>
        Ein illustratives Beispiel: Aus einer Grundgesamtheit von mehreren tausend
        Firmen bleibt nach sauberer Anwendung der Kriterien oft nur ein kleiner Kern
        wirklich passender Zielaccounts übrig. Genau dieser Kern ist die Liste, auf
        die Sie Ads, Outbound und Telefon ausrichten.
      </p>
      <p>
        Und hier schließt sich der Kreis zur telefonischen Qualifizierung: Eine
        Zielliste sagt, <em>wen</em> Sie ansprechen. Ob dort gerade echter Bedarf
        besteht, verrät erst das Gespräch. ABM ohne diese Qualifizierung bleibt
        unvollständig. Eine gut gefilterte Liste ohne den letzten Schritt ist nur
        eine bessere Vermutung. Wie die Zielliste anschließend in ein planbares
        System aus Ads, Outbound und Telefon übergeht, lesen Sie in unserem Beitrag
        zur B2B-Leadgenerierung über LinkedIn.
      </p>
    </>
  );
}
